import React, { useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { getSuggestedExpiration } from 'src/redux/shopping_cart/thunks';
import { setExpirationWindowMonths } from 'src/redux/shopping_cart/shopping_cart.slice';
import { selectDataSuggested, selectProfileCompletenessNotice } from 'src/redux/shopping_cart/shopping_cart.selectors';
import {
    formatExpirationMonthKey,
    summarizeActiveProductsByExpiration,
} from 'src/helpers/activeProductsByExpiration';
import Icon from 'src/components/common/Icon';
import Modal from 'src/components/common/modal/Modal';
import UserProfileEditModal from '../calculo-sugeridos/suggested-form/UserProfileEditModal';
import { ProfileNoticeAlert } from '../calculo-sugeridos/styles';
import ContactIcon, { ContactIconName } from './UserInfoIcons';
import {
    AccordionBar,
    AccordionContent,
    ChipIcon,
    ChipLabel,
    ChipMain,
    Column,
    ContactCard,
    ContactCardHeader,
    ContactColumns,
    EditButton,
    EmptyExpiration,
    ExpirationBarControl,
    ExpirationBarLeft,
    ExpirationControlLabel,
    ExpirationSelect,
    ExpirationTable,
    Footer,
    InfoRow,
    InfoRowLabel,
    InfoRowValue,
    MetricLabel,
    MetricRow,
    MetricsGrid,
    MetricValue,
    OtpBadge,
    OuterBody,
    OuterCard,
    OuterHeader,
    PcomProductsRow,
    ProductCell,
    ProductChip,
    SectionPill,
    StatusBadge,
    StatusPill,
    TableWrapper,
} from './UserInfoSummary.styles';

/** Ventanas de vencimiento (en meses) que el usuario puede elegir en el dropdown. */
export const EXPIRATION_MONTHS_OPTIONS = [3, 6, 9, 12] as const;

/** Ventana por defecto (meses): siempre se consultan los primeros 3 meses. */
export const DEFAULT_EXPIRATION_MONTHS = 3;

/** Mensaje cuando no hay productos por vencer, en función de la ventana elegida. */
export const emptyExpiringProductsCopy = (months: number): string =>
    `No hay productos por vencer en los próximos ${months} meses.`;

export const EMPTY_EXPIRING_PRODUCTS_COPY = emptyExpiringProductsCopy(
    DEFAULT_EXPIRATION_MONTHS
);

export const FOOTER_COPY =
    'La información mostrada corresponde al estado actual del anunciante en Propiedades.com.';

/**
 * Columnas de productos del cronograma de expiración.
 * - `key`: clave para buscar el conteo del mes (proviene del helper).
 * - `header`: texto visible de la columna.
 * - `asStatus`: muestra "Activo" en vez del conteo cuando hay dato.
 */
const EXPIRATION_PRODUCT_COLUMNS = [
    { key: 'Primes', header: 'Primes', asStatus: false, center: true },
    { key: 'Destacados', header: 'Destacados', asStatus: false, center: true },
    { key: 'Elite', header: 'Elite', asStatus: true, center: true },
    // "Oportunidades ilimitadas": se alimenta de los ítems name_product "OI" de
    // active_products_by_expiration_month que vencen en los próximos 90 días.
    { key: 'Oportunidades', header: 'Oportunidades ilimitadas', asStatus: true, center: true },
] as const;

/** `name_product` de "Oportunidades ilimitadas" en active_products_by_expiration_month. */
const OI_NAME_PRODUCT = 'OI';

/** Texto de la celda de producto: "[ - ]" vacío, "Activo" si es de estado, o "[ N ]". */
const expirationCellText = (count: number, asStatus: boolean): string => {
    if (count === 0) return '[ - ]';
    if (asStatus) return 'Activo';
    return `[ ${count} ]`;
};

/**
 * True si `endDate` (YYYY-MM-DD...) vence entre hoy y los próximos `months` meses.
 * Replica la ventana del backend (DATE_ADD(CURDATE(), INTERVAL N MONTH)).
 */
const expiresWithinMonths = (
    endDate: string | null | undefined,
    months: number
): boolean => {
    if (!endDate) return false;
    const end = new Date(endDate);
    if (Number.isNaN(end.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const limit = new Date(today);
    limit.setMonth(limit.getMonth() + months);
    return end >= today && end <= limit;
};

/** Clave `YYYY-MM` del mes actual (para marcar el estado "Próximo a vencer"). */
const currentMonthKey = (): string => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

/** Fila etiqueta → valor de la sección "Contacto básico". */
const ContactInfoRow = ({
    icon,
    label,
    value,
    verified,
    active,
    bold,
    badgeLeading,
}: {
    icon: ContactIconName;
    label: string;
    value: React.ReactNode;
    verified?: boolean;
    /** Estado informativo (solo lectura): muestra badge "Activo"/"Inactivo". */
    active?: boolean;
    bold?: boolean;
    /**
     * Si es `true`, el badge de validación (OTP / Sin validar) se muestra ANTES
     * del valor. Se usa en los correos para que el texto quede alineado a la
     * derecha con el resto de los datos (el badge no lo desplaza).
     */
    badgeLeading?: boolean;
}) => {
    const otpBadge = verified !== undefined && (
        <OtpBadge $verified={verified}>
            <ContactIcon name={verified ? 'check' : 'close'} size={13} />
            {verified ? 'OTP' : 'Sin validar'}
        </OtpBadge>
    );

    return (
        <InfoRow>
            <InfoRowLabel>
                <ContactIcon className="row-icon" name={icon} size={16} />
                {label}
            </InfoRowLabel>
            <InfoRowValue $bold={bold}>
                {badgeLeading && otpBadge}
                {value}
                {!badgeLeading && otpBadge}
                {active !== undefined && (
                    <StatusPill $on={active}>
                        {active && <ContactIcon name="check" size={13} />}
                        {active ? 'Activo' : 'Inactivo'}
                    </StatusPill>
                )}
            </InfoRowValue>
        </InfoRow>
    );
};

// A diferencia de los getters numéricos del componente, distingue "no hay dato"
// de un cero real: el score lo calcula un cron y las publicaciones recientes
// llegan sin valor.
const readOptionalNumber = (
    source: Record<string, unknown>,
    key: string
): number | null => {
    const value = source[key];
    return typeof value === 'number' ? value : null;
};

// Métricas de score ya resueltas a texto, con el caso "todavía sin score" incluido.
const buildScoreMetrics = (source: Record<string, unknown>) => {
    const average = readOptionalNumber(source, 'avg_publication_score');
    const highScore = readOptionalNumber(source, 'high_score_properties');
    const scored = readOptionalNumber(source, 'scored_active_properties');
    const hasHighScoreData = highScore !== null && scored !== null && scored > 0;
    return {
        average: average === null ? 'Sin datos' : String(Math.round(average)),
        highScore: hasHighScoreData ? `${highScore} de ${scored}` : 'Sin datos',
    };
};

/**
 * Información del usuario (tab "Información del usuario").
 * Se nutre de `suggested_calculator` (estado `shopping_cart.suggested`).
 */
const UserInfoSummary = () => {
    const [showModal, setShowModal] = useState(false);
    const [outerOpen, setOuterOpen] = useState(true);
    const [contactOpen, setContactOpen] = useState(true);
    const [pcomOpen, setPcomOpen] = useState(false);
    const [expirationOpen, setExpirationOpen] = useState(false);

    const dispatch = useAppDispatch();
    const shoppingCart = useAppSelector(selectDataSuggested);
    const { suggested } = shoppingCart;
    // Ventana de vencimiento (meses). Vive en redux para sobrevivir al re-fetch;
    // por defecto 3 (primeros ~90 días).
    const expirationMonths =
        shoppingCart.expirationWindowMonths ?? DEFAULT_EXPIRATION_MONTHS;
    const expirationLoading = shoppingCart.expirationLoading ?? false;
    const profileNotice = useAppSelector(selectProfileCompletenessNotice);

    const expirationSummaries = useMemo(
        () => summarizeActiveProductsByExpiration(suggested.active_products_by_expiration_month),
        [suggested.active_products_by_expiration_month]
    );

    const profile = suggested.user_profile;
    // suggested_calculator distribuye los campos en distintos niveles:
    // - user_profile: broker_type, freemium_state, has_active_elite, etc.
    // - portfolio_metrics: total_properties, total_active_properties, etc.
    // - a veces en la raíz (ver redux/shopping_cart/api.tsx).
    // Fusionamos las tres fuentes para que los valores siempre se muestren.
    const source = useMemo(() => {
        const root = (suggested as unknown) as Record<string, unknown>;
        const userProfile = ((profile ?? {}) as unknown) as Record<string, unknown>;
        const portfolioMetrics =
            (userProfile.portfolio_metrics as Record<string, unknown> | undefined) ??
            (root.portfolio_metrics as Record<string, unknown> | undefined) ??
            {};
        return {
            ...root,
            ...userProfile,
            ...portfolioMetrics,
        };
    }, [suggested, profile]);

    const getNumber = (key: string): number => {
        const value = source[key];
        return typeof value === 'number' ? value : 0;
    };
    const getText = (key: string): string => {
        const value = source[key];
        return typeof value === 'string' && value !== '' ? value : '-';
    };
    // Devuelve solo la fecha (YYYY-MM-DD), sin la hora.
    const getDate = (key: string): string => {
        const value = source[key];
        if (typeof value !== 'string' || value === '') return '-';
        return value.split('T')[0];
    };

    // "Producto Elite": Sí/No según has_active_elite (acepta boolean, 1/0 o "true").
    const eliteRaw = source.has_active_elite;
    const hasActiveElite = eliteRaw === true || eliteRaw === 1 || eliteRaw === 'true';

    // "Oportunidades ilimitadas": Sí/No según has_active_oi (acepta boolean, 1/0 o "true").
    const oiRaw = source.has_active_oi;
    const hasActiveOi = oiRaw === true || oiRaw === 1 || oiRaw === 'true';

    // "Prime": Sí/No según has_active_prime (acepta boolean, 1/0 o "true").
    // Refleja que el usuario tiene al menos un Prime activo, sin depender de la
    // ventana de vencimiento de active_products_by_expiration_month.
    const primeRaw = source.has_active_prime;
    const hasActivePrime = primeRaw === true || primeRaw === 1 || primeRaw === 'true';

    const userId = suggested.users.user_id;
    const name = suggested.user_profile?.name || '';
    const lastname = suggested.user_profile?.lastname || '';
    const email = suggested.users.email || '';
    const phoneContact = suggested.user_profile?.phone_contact || '';
    const phoneWhatsapp = suggested.user_profile?.phone_whatsapp || '';
    const brokerType = getText('broker_type');
    const sellerEmail = suggested.pipedrive_seller?.email || '';
    const feedName = suggested.user_profile?.feed_name || '';
    const isCrm =
        suggested.user_profile?.from_xml === 1 && suggested.user_profile?.feed_name !== '';

    // Canales de contacto verificados por OTP (otp_channel_verifications).
    const otp = suggested.user_profile?.otp_channel_verifications;

    const emailContact = otp?.email_contact;
    const emailContactValue = emailContact?.value || '';
    const emailContactVerified = !!(emailContact?.verified && emailContact.value);

    const accountEmailVerified = !!(otp?.personal_email?.verified && otp.personal_email.value);

    // Teléfono de contacto: valor verificado por OTP o, en su defecto, phone_contact.
    const smsOtp = otp?.sms;
    const telContactVerified = !!(smsOtp?.verified && smsOtp.value);
    const telContactValue = telContactVerified ? smsOtp!.value : phoneContact;

    // WhatsApp: valor verificado por OTP o, en su defecto, phone_whatsapp.
    const whatsappOtp = otp?.whatsapp;
    const whatsappVerified = !!(whatsappOtp?.verified && whatsappOtp.value);
    const whatsappValue = whatsappVerified ? whatsappOtp!.value : phoneWhatsapp;

    // Meses con un producto OI ("Oportunidades ilimitadas") que vence dentro de la
    // ventana seleccionada (3/6/12 meses).
    const oiExpiringByMonth = useMemo(() => {
        const map: Record<string, boolean> = {};
        Object.entries(suggested.active_products_by_expiration_month ?? {}).forEach(
            ([monthKey, items]) => {
                const hasOiSoon = (items ?? []).some(
                    (item) =>
                        (item?.name_product ?? '').trim() === OI_NAME_PRODUCT &&
                        expiresWithinMonths(item?.end_date, expirationMonths)
                );
                if (hasOiSoon) map[monthKey] = true;
            }
        );
        return map;
    }, [suggested.active_products_by_expiration_month, expirationMonths]);

    // Cronograma de expiración: cuenta por mes y por tipo de producto.
    const expirationRows = useMemo(() => {
        const nowKey = currentMonthKey();
        const byMonth = new Map<string, { monthLabel: string; counts: Record<string, number> }>();

        // Productos contados por el helper (Primes/Destacados/Elite/Premium).
        expirationSummaries.forEach(({ monthKey, monthLabel, rows }) => {
            const counts: Record<string, number> = {};
            rows.forEach(({ label, count }) => {
                counts[label] = count;
            });
            byMonth.set(monthKey, { monthLabel, counts });
        });

        // "Oportunidades ilimitadas": marca el mes como activo si vence dentro de la ventana.
        Object.keys(oiExpiringByMonth).forEach((monthKey) => {
            const existing = byMonth.get(monthKey);
            if (existing) {
                existing.counts.Oportunidades = 1;
            } else {
                byMonth.set(monthKey, {
                    monthLabel: formatExpirationMonthKey(monthKey),
                    counts: { Oportunidades: 1 },
                });
            }
        });

        return Array.from(byMonth.entries())
            .sort(([a], [b]) => a.localeCompare(b, 'es-MX'))
            .map(([monthKey, { monthLabel, counts }]) => ({
                monthKey,
                monthLabel,
                counts,
                isExpiringSoon: monthKey <= nowKey,
            }))
            // El helper aún cuenta Premium (lo usa el formulario antiguo), pero esa
            // columna ya no se muestra aquí: omite los meses sin productos visibles.
            .filter(({ counts }) =>
                EXPIRATION_PRODUCT_COLUMNS.some(({ key }) => (counts[key] ?? 0) > 0)
            );
    }, [expirationSummaries, oiExpiringByMonth]);

    // Productos que el usuario tiene activos (según active_products_by_expiration_month).
    const activeProductNames = useMemo(() => {
        const set = new Set<string>();
        Object.values(suggested.active_products_by_expiration_month ?? {}).forEach((items) => {
            (items ?? []).forEach((item) => {
                if (item?.name_product) set.add(item.name_product.trim());
            });
        });
        return set;
    }, [suggested.active_products_by_expiration_month]);

    // Productos PCOM (solo lectura, reflejan el estado del usuario).
    const pcomProducts: { key: string; label: string; icon: ContactIconName; on: boolean }[] = [
        { key: 'oportunidades', label: 'Oportunidades ilimitadas', icon: 'infinity', on: hasActiveOi },
        {
            key: 'destacados',
            label: 'Destacados',
            icon: 'star',
            on: activeProductNames.has('Slot') || getNumber('highlighted_properties') > 0,
        },
        { key: 'prime', label: 'Prime', icon: 'crown', on: hasActivePrime || activeProductNames.has('Slot destacado') },
        { key: 'elite', label: 'Elite', icon: 'gem', on: hasActiveElite },
    ];

    // Fecha en formato DD-MM-YYYY.
    const formatDateDMY = (key: string): string => {
        const value = getDate(key);
        if (value === '-') return '-';
        const [y, m, d] = value.split('-');
        return y && m && d ? `${d}-${m}-${y}` : value;
    };

    const scoreMetrics = buildScoreMetrics(source);

    // Al cambiar la ventana (3/6/9/12 meses) se re-consulta el suggested_calculator
    // solo para refrescar los productos por vencer de esa ventana (sin recargar
    // toda la vista ni reiniciar la selección del carrito).
    const handleExpirationMonthsChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const months = Number(event.target.value);
        dispatch(setExpirationWindowMonths(months));
        if (email) {
            dispatch(
                getSuggestedExpiration({
                    country: 'MX',
                    email,
                    type_user: 2,
                    expiration_months: months,
                })
            );
        }
    };

    return (
        <>
            <OuterCard>
                <OuterHeader type="button" onClick={() => setOuterOpen((v) => !v)}>
                    <span className="header-title">
                        <Icon icon="pcom-icon-user" size={18} />
                        Información del anunciante{email ? ` · ${email}` : ''}
                    </span>
                    <Icon
                        className="header-chevron"
                        icon={outerOpen ? 'pcom-icon-chevron-up' : 'pcom-icon-chevron-down'}
                        size={20}
                    />
                </OuterHeader>

                {outerOpen && (
                    <OuterBody>
                        {/* 1. Contacto básico */}
                        <ContactCard>
                            <ContactCardHeader>
                                <SectionPill
                                    type="button"
                                    onClick={() => setContactOpen((v) => !v)}
                                >
                                    Contacto básico
                                    <Icon
                                        className="pill-chevron"
                                        icon={contactOpen ? 'pcom-icon-chevron-up' : 'pcom-icon-chevron-down'}
                                        size={16}
                                    />
                                </SectionPill>
                                <EditButton
                                    type="button"
                                    data-id="button-open-modal"
                                    onClick={() => setShowModal(true)}
                                >
                                    <ContactIcon name="user" size={16} />
                                    Editar perfil
                                </EditButton>
                            </ContactCardHeader>

                            {profileNotice && (
                                <ProfileNoticeAlert>
                                    <Icon icon="pcom-icon-warning-filled" size={16} />
                                    {profileNotice}
                                </ProfileNoticeAlert>
                            )}

                            {contactOpen && (
                                <ContactColumns>
                                    <Column>
                                        <ContactInfoRow
                                            icon="id"
                                            label="ID"
                                            value={`#${userId}`}
                                        />
                                        {name && (
                                            <ContactInfoRow
                                                icon="user"
                                                label="Nombre"
                                                value={name}
                                            />
                                        )}
                                        {lastname && (
                                            <ContactInfoRow
                                                icon="user-square"
                                                label="Apellido"
                                                value={lastname}
                                            />
                                        )}
                                        {emailContactValue && (
                                            <ContactInfoRow
                                                icon="mail"
                                                label="Email contacto"
                                                value={emailContactValue}
                                                verified={emailContactVerified}
                                                badgeLeading
                                            />
                                        )}
                                        {email && (
                                            <ContactInfoRow
                                                icon="mail"
                                                label="Email de la cuenta"
                                                value={email}
                                                verified={accountEmailVerified}
                                                badgeLeading
                                            />
                                        )}
                                    </Column>
                                    <Column>
                                        {telContactValue && (
                                            <ContactInfoRow
                                                icon="phone"
                                                label="Tel contacto"
                                                value={telContactValue}
                                                verified={telContactVerified}
                                            />
                                        )}
                                        {whatsappValue && (
                                            <ContactInfoRow
                                                icon="chat"
                                                label="WhatsApp"
                                                value={whatsappValue}
                                                verified={whatsappVerified}
                                            />
                                        )}
                                        {feedName && (
                                            <ContactInfoRow
                                                icon="rss"
                                                label="Feed"
                                                value={feedName}
                                                active={isCrm}
                                                bold
                                            />
                                        )}
                                        {brokerType !== '-' && (
                                            <ContactInfoRow
                                                icon="building"
                                                label="Tipo de broker"
                                                value={brokerType}
                                            />
                                        )}
                                        {sellerEmail && (
                                            <ContactInfoRow
                                                icon="briefcase"
                                                label="Gestor comercial"
                                                value={sellerEmail}
                                            />
                                        )}
                                    </Column>
                                </ContactColumns>
                            )}
                        </ContactCard>

                        {/* 2. Información PCOM (diseño provisional, pendiente de rediseño) */}
                        <div>
                            <AccordionBar
                                type="button"
                                $open={pcomOpen}
                                onClick={() => setPcomOpen((v) => !v)}
                            >
                                Información PCOM
                                <Icon
                                    className="bar-chevron"
                                    icon={pcomOpen ? 'pcom-icon-chevron-up' : 'pcom-icon-chevron-down'}
                                    size={20}
                                />
                            </AccordionBar>
                            {pcomOpen && (
                                <AccordionContent>
                                    <PcomProductsRow>
                                        {pcomProducts.map(({ key, label, icon, on }) => (
                                            <ProductChip key={key} $on={on}>
                                                <ChipMain>
                                                    <ChipIcon $on={on}>
                                                        <ContactIcon name={icon} size={16} />
                                                    </ChipIcon>
                                                    <ChipLabel>{label}</ChipLabel>
                                                </ChipMain>
                                                <StatusPill
                                                    $on={on}
                                                    data-id={`pcom-status-${key}`}
                                                    aria-label={`${label}: ${on ? 'Activo' : 'Inactivo'}`}
                                                >
                                                    {on && <ContactIcon name="check" size={13} />}
                                                    {on ? 'Activo' : 'Inactivo'}
                                                </StatusPill>
                                            </ProductChip>
                                        ))}
                                    </PcomProductsRow>

                                    <MetricsGrid>
                                        <MetricRow>
                                            <MetricLabel>Total propiedades</MetricLabel>
                                            <MetricValue>{getNumber('total_properties')}</MetricValue>
                                        </MetricRow>
                                        <MetricRow>
                                            <MetricLabel>Total propiedades activas</MetricLabel>
                                            <MetricValue>{getNumber('total_active_properties')}</MetricValue>
                                        </MetricRow>
                                        <MetricRow>
                                            <MetricLabel>Activas residenciales</MetricLabel>
                                            <MetricValue>{getNumber('active_residential_properties')}</MetricValue>
                                        </MetricRow>
                                        <MetricRow>
                                            <MetricLabel>Slots vacíos sin asignación</MetricLabel>
                                            <MetricValue>{getNumber('unassigned_active_slots')}</MetricValue>
                                        </MetricRow>
                                        <MetricRow>
                                            <MetricLabel>Total propiedades destacadas</MetricLabel>
                                            <MetricValue>{getNumber('highlighted_properties')}</MetricValue>
                                        </MetricRow>
                                        <MetricRow>
                                            <MetricLabel>Fuera de mercado</MetricLabel>
                                            <MetricValue>{getNumber('out_of_market_properties')}</MetricValue>
                                        </MetricRow>
                                        <MetricRow $highlight>
                                            <MetricLabel>Score promedio de inventario activo</MetricLabel>
                                            <MetricValue $highlight>{scoreMetrics.average}</MetricValue>
                                        </MetricRow>
                                        <MetricRow>
                                            <MetricLabel>Cantidad de propiedades activas que tienen score sobre &gt;= 80</MetricLabel>
                                            <MetricValue>{scoreMetrics.highScore}</MetricValue>
                                        </MetricRow>
                                        <MetricRow>
                                            <MetricLabel>Zona principal de publicación</MetricLabel>
                                            <MetricValue>{getText('main_publication_zone')}</MetricValue>
                                        </MetricRow>
                                        <MetricRow>
                                            <MetricLabel>Fecha última publicación</MetricLabel>
                                            <MetricValue>{formatDateDMY('last_publication_date')}</MetricValue>
                                        </MetricRow>
                                        <MetricRow>
                                            <MetricLabel>Posibles Elite</MetricLabel>
                                            <MetricValue>{getNumber('elite_candidate_properties')}</MetricValue>
                                        </MetricRow>
                                        <MetricRow>
                                            <MetricLabel>Leads ofuscados</MetricLabel>
                                            <MetricValue>{getNumber('blocked_leads')}</MetricValue>
                                        </MetricRow>
                                    </MetricsGrid>
                                </AccordionContent>
                            )}
                        </div>

                        {/* 3. Productos a expirar (tabla sin cambios) */}
                        <div>
                            <AccordionBar
                                as="div"
                                role="button"
                                tabIndex={0}
                                $open={expirationOpen}
                                onClick={() => setExpirationOpen((v) => !v)}
                                onKeyDown={(e) => {
                                    // Solo alterna cuando el foco está en la barra (no en el select).
                                    if (
                                        e.target === e.currentTarget &&
                                        (e.key === 'Enter' || e.key === ' ')
                                    ) {
                                        e.preventDefault();
                                        setExpirationOpen((v) => !v);
                                    }
                                }}
                            >
                                <ExpirationBarLeft>
                                    Productos a expirar
                                    <ExpirationBarControl
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <ExpirationControlLabel htmlFor="expiration-months-select">
                                            Ventana
                                        </ExpirationControlLabel>
                                        <ExpirationSelect
                                            id="expiration-months-select"
                                            data-id="expiration-months-select"
                                            aria-label="Ventana de vencimiento"
                                            value={expirationMonths}
                                            disabled={expirationLoading}
                                            onChange={handleExpirationMonthsChange}
                                        >
                                            {EXPIRATION_MONTHS_OPTIONS.map((months) => (
                                                <option key={months} value={months}>
                                                    {months} meses
                                                </option>
                                            ))}
                                        </ExpirationSelect>
                                    </ExpirationBarControl>
                                </ExpirationBarLeft>
                                <Icon
                                    className="bar-chevron"
                                    icon={expirationOpen ? 'pcom-icon-chevron-up' : 'pcom-icon-chevron-down'}
                                    size={20}
                                />
                            </AccordionBar>
                            {expirationOpen && (
                                <AccordionContent>
                                    {expirationRows.length === 0 ? (
                                        <EmptyExpiration>
                                            {emptyExpiringProductsCopy(expirationMonths)}
                                        </EmptyExpiration>
                                    ) : (
                                        <TableWrapper>
                                            <ExpirationTable>
                                                <thead>
                                                    <tr>
                                                        <th>Mes de expiración</th>
                                                        {EXPIRATION_PRODUCT_COLUMNS.map(({ key, header, center }) => (
                                                            <th key={key} className={center ? 'center' : undefined}>
                                                                {header}
                                                            </th>
                                                        ))}
                                                        <th>Estado</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {expirationRows.map(({ monthKey, monthLabel, counts, isExpiringSoon }) => (
                                                        <tr key={monthKey}>
                                                            <td className="month">{monthLabel}</td>
                                                            {EXPIRATION_PRODUCT_COLUMNS.map(({ key, asStatus, center }) => {
                                                                const count = counts[key] ?? 0;
                                                                const isEmpty = count === 0;
                                                                return (
                                                                    <td key={key} className={center ? 'center' : undefined}>
                                                                        <ProductCell
                                                                            data-empty={isEmpty}
                                                                            data-active={!isEmpty && asStatus}
                                                                        >
                                                                            {expirationCellText(count, asStatus)}
                                                                        </ProductCell>
                                                                    </td>
                                                                );
                                                            })}
                                                            <td>
                                                                <StatusBadge $danger={isExpiringSoon}>
                                                                    {isExpiringSoon ? 'Próximo a vencer' : 'Estable'}
                                                                </StatusBadge>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </ExpirationTable>
                                        </TableWrapper>
                                    )}
                                </AccordionContent>
                            )}
                        </div>

                        <Footer>
                            <Icon className="footer-icon" icon="pcom-icon-check-circle" size={16} />
                            {FOOTER_COPY}
                        </Footer>
                    </OuterBody>
                )}
            </OuterCard>

            {showModal && email !== '' && (
                <Modal onClose={() => setShowModal(false)}>
                    <UserProfileEditModal onClose={() => setShowModal(false)} />
                </Modal>
            )}
        </>
    );
};

export default UserInfoSummary;
